// ── OrgRiskAcceptancesIndexPage ───────────────────────────────────────────────
//
// Route: /org-risk-acceptances
//
// Parallel to RiskAcceptancesIndexPage (D-ORA-ARCH-7 structural parallelism).
// ORA-specific differences:
//   - No vendor filter (org = current tenant; no vendor_id on ORA entries)
//   - Uses ORA hooks + types
//   - Action modal uses useOrgRiskAcceptanceAction
//   - Navigation paths: /org-risk-acceptances/*
//
// ORA.9: renewal chain grouping via OrgRenewalChainGroup (AC-ORA9-OGROUP-*)

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  Search,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RiskAcceptanceActionModal } from '@/components/shared/RiskAcceptanceActionModal';
import { useAuthStore } from '@/store/auth.store';
import { useOrgRiskAcceptances, useOrgRiskAcceptanceAction, useOraOverview } from '@/api/hooks/useOrgRiskAcceptances';
import { OrgRenewalChainGroup, OrgOrphanChildRow } from '@/components/org-risk-acceptances/OrgRenewalChainGroup';
import { OrgRaOverviewSection } from '@/components/org-risk-acceptances/OrgRaOverviewSection';
import type { OraEntry, OraActionType } from '@/api/types/org-risk-acceptance.types';

// ── groupOraItems — ORA.9 renewal chain grouping ──────────────────────────────
//
// Groups flat item array by parent_acceptance_id.
// Returns: { groups: { parent, children }[]; orphans: OraEntry[] }
//   groups: parent entries + their children present in the same page
//   orphans: child entries whose parent is not in the current items array

function groupOraItems(items: OraEntry[]): {
  groups: { parent: OraEntry; children: OraEntry[] }[];
  orphans: OraEntry[];
} {
  const idSet = new Set(items.map((e) => e.id));
  const parentMap = new Map<string, OraEntry[]>();

  for (const item of items) {
    if (item.parent_acceptance_id !== null && idSet.has(item.parent_acceptance_id)) {
      const arr = parentMap.get(item.parent_acceptance_id) ?? [];
      arr.push(item);
      parentMap.set(item.parent_acceptance_id, arr);
    }
  }

  const childIds = new Set<string>();
  for (const children of parentMap.values()) {
    for (const c of children) childIds.add(c.id);
  }

  const groups: { parent: OraEntry; children: OraEntry[] }[] = [];
  const orphans: OraEntry[] = [];

  for (const item of items) {
    if (childIds.has(item.id)) continue;
    if (item.parent_acceptance_id !== null && !idSet.has(item.parent_acceptance_id)) {
      orphans.push(item);
    } else {
      groups.push({ parent: item, children: parentMap.get(item.id) ?? [] });
    }
  }

  return { groups, orphans };
}

// ── Status filter options (⚠ O4a reads-flip: native lifecycle axis) ────────────
//
// Each option maps to a native workflow_status (+ closure_reason for the closed variants — the legacy
// closed_mitigated/revoked/expired all fold into workflow_status='closed' + a distinct closure_reason).
const STATUS_OPTIONS = [
  { key: '',                  label: 'All Statuses',        workflow_status: '',                 closure_reason: '' },
  { key: 'draft',             label: 'Draft',               workflow_status: 'draft',            closure_reason: '' },
  { key: 'pending_approval',  label: 'Pending Approval',    workflow_status: 'pending_approval', closure_reason: '' },
  { key: 'approved',          label: 'Approved',            workflow_status: 'approved',         closure_reason: '' },
  { key: 'denied',            label: 'Denied',              workflow_status: 'denied',           closure_reason: '' },
  { key: 'returned',          label: 'Returned',            workflow_status: 'returned',         closure_reason: '' },
  { key: 'closed_remediated', label: 'Closed (Remediated)', workflow_status: 'closed',           closure_reason: 'remediated' },
  { key: 'revoked',           label: 'Revoked',             workflow_status: 'closed',           closure_reason: 'revoked' },
  { key: 'expired',           label: 'Expired',             workflow_status: 'closed',           closure_reason: 'expired' },
] as const;

// ── RR-BI-1 final parity: workflow_status → human label for the active-filter chips (mirrors the vendor
// page's STATUS_LABEL). ──
const WORKFLOW_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', pending_approval: 'Pending approval', approved: 'Approved',
  denied: 'Denied', returned: 'Returned', closed: 'Closed', withdrawn: 'Withdrawn',
};

// Reverse-map the two native filter vars → the dropdown's selected key. A multi-value drill (e.g. the
// dashboard's workflow_status=returned,denied) has no single-option match → the dropdown reads "All"
// while the list still filters natively on the comma-separated value.
function statusKeyFromFilter(ws: string, cr: string): string {
  return STATUS_OPTIONS.find((o) => o.workflow_status === ws && o.closure_reason === cr)?.key ?? '';
}

// ── RR-BI-1 Slice 1: page-size 25/50/100/All (mirror vendor). "All" → the backend cap (limit.max(100))
// with an honest note; true-unbounded rides B-VENDORS-LIST-VIRTUALIZATION-DEFERRED. ──
const PAGE_SIZES: (number | 'All')[] = [25, 50, 100, 'All'];
const MAX_PAGE_SIZE = 100;

function PageSizeSelect({ value, onChange }: { value: number | 'All'; onChange: (n: number | 'All') => void }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-text-secondary">Show</span>
      {PAGE_SIZES.map((n) => (
        <button key={String(n)} type="button" onClick={() => onChange(n)}
          title={n === 'All' ? `Up to ${MAX_PAGE_SIZE} (server cap; full-list view coming)` : undefined}
          className={cn('text-[11px] font-semibold rounded px-1.5 py-0.5 transition-colors',
            value === n ? 'bg-accent-cyan/15 text-accent-cyan' : 'text-text-secondary hover:text-text-primary')}>
          {n}
        </button>
      ))}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function OrgRiskAcceptancesIndexPage() {
  useEffect(() => { document.title = 'Risk Register — VeriGRC'; }, []);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, hasAnyRole } = useAuthStore();

  // Role checks
  const canCreate = hasAnyRole(['risk_manager', 'super_admin']);
  const isApproverClass = hasAnyRole(['risk_manager', 'super_admin']);
  const currentUserId = user?.id ?? null;

  // Filters — ⚠ O4a reads-flip: native lifecycle filters from URL params (tile/chart drill-down support)
  const [workflowStatusFilter, setWorkflowStatusFilter] = useState(() => searchParams.get('workflow_status') ?? '');
  const [closureReasonFilter, setClosureReasonFilter] = useState(() => searchParams.get('closure_reason') ?? '');
  const [riskCategoryFilter, setRiskCategoryFilter] = useState(() => searchParams.get('risk_category') ?? '');
  const [approachingExpiry, setApproachingExpiry] = useState(() => searchParams.get('approaching_expiry') === 'true');
  // DASH-CLEANUP.3 renewed drill + ⚠ RR-UX-1 S2 item-6: TRI-STATE (true = has renewals · false = no renewal,
  // the Lapsed drill · undefined = no filter). Mirrors the vendor page; the boolean model couldn't send false.
  const [hasRenewals, setHasRenewals] = useState<boolean | undefined>(() => {
    const v = searchParams.get('has_renewals');
    return v === 'true' ? true : v === 'false' ? false : undefined;
  });
  // ⚠ RR-BI-1 Slice 1: requires-my-action toggle + on-demand search (committed on button/Enter, not per-key)
  const [requiresAction, setRequiresAction] = useState(() => searchParams.get('requires_action_by_user') === 'true');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const [committedSearch, setCommittedSearch] = useState(() => searchParams.get('search') ?? '');
  const [pageChoice, setPageChoice] = useState<number | 'All'>(25);
  const pageSize = pageChoice === 'All' ? MAX_PAGE_SIZE : pageChoice;
  const [offset, setOffset] = useState(0);

  // ORA.9: action type sourced from OraActionType (full set incl. close/revoke/renewal actions)
  const [actionModal, setActionModal] = useState<{
    action: OraActionType;
    entry: OraEntry;
  } | null>(null);

  // Data query
  const { data: result, isLoading } = useOrgRiskAcceptances({
    workflow_status: workflowStatusFilter || undefined,
    closure_reason: closureReasonFilter || undefined,
    search: committedSearch || undefined,
    requires_action_by_user: requiresAction || undefined,
    approaching_expiry: approachingExpiry || undefined,
    has_renewals: hasRenewals,
    risk_category: riskCategoryFilter || undefined,
    limit: pageSize,
    offset,
    sort_by: 'updated_at',
    sort_order: 'desc',
  });

  const items = result?.items ?? [];
  const total = result?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const currentPage = Math.floor(offset / pageSize) + 1;
  const anyFilter = !!(workflowStatusFilter || closureReasonFilter || committedSearch || requiresAction || approachingExpiry || hasRenewals !== undefined || riskCategoryFilter);

  function submitSearch() { setCommittedSearch(searchInput.trim()); setOffset(0); }

  // ── RR-BI-1 Slice 2b: operational BI aggregate + the drill-through bridge to this page's LOCAL filter
  // state (the ORA page owns filters in component state, not the URL like the vendor page). ──
  const { data: overview, isLoading: overviewLoading } = useOraOverview();

  const statusSet = new Set(workflowStatusFilter.split(',').filter(Boolean));

  // applyBiFilter maps the section's generic (key,value) drill-throughs onto the page's local setters.
  function applyBiFilter(key: string, value: string | null) {
    switch (key) {
      case 'requires_action_by_user': setRequiresAction(value === 'true'); break;
      case 'approaching_expiry':      setApproachingExpiry(value === 'true'); break;
      case 'has_renewals':            setHasRenewals(value == null ? undefined : value === 'true'); break;
      case 'workflow_status':         setWorkflowStatusFilter(value ?? ''); break;
      case 'closure_reason':          setClosureReasonFilter(value ?? ''); break;
      case 'risk_category':           setRiskCategoryFilter(value ?? ''); break;
    }
    setOffset(0);
  }

  // toggleStatus stacks/unstacks a workflow_status in the composable (comma-separated) filter — mirrors the
  // vendor strip's multi-select facet.
  function toggleStatus(s: string) {
    const next = new Set(statusSet);
    if (next.has(s)) next.delete(s); else next.add(s);
    setWorkflowStatusFilter(next.size ? [...next].join(',') : '');
    setOffset(0);
  }

  // ── RR-BI-1 final parity: active-filter CHIPS (mirror the vendor page). One chip per active filter VALUE,
  // read from the SAME local state the strip + list read → strip ↔ chip cannot drift (one source). ⚠ The
  // free-text search is NOT a chip (it lives in the input); a KPI/strip click is a FILTER, never text search.
  const chipEntries: [string, string][] = [];
  for (const s of statusSet) chipEntries.push(['workflow_status', s]);
  if (closureReasonFilter) chipEntries.push(['closure_reason', closureReasonFilter]);
  if (approachingExpiry) chipEntries.push(['approaching_expiry', 'true']);
  if (hasRenewals !== undefined) chipEntries.push(['has_renewals', String(hasRenewals)]);
  if (riskCategoryFilter) chipEntries.push(['risk_category', riskCategoryFilter]);

  function chipLabel(key: string, value: string): string {
    if (key === 'workflow_status') return WORKFLOW_STATUS_LABEL[value] ?? value;
    if (key === 'closure_reason') return `Closed · ${value.replace(/_/g, ' ')}`;
    if (key === 'approaching_expiry') return 'Expiring ≤30d';
    if (key === 'has_renewals') return value === 'false' ? 'No renewal' : 'Has renewals';
    if (key === 'risk_category') return `Category · ${value.replace(/_/g, ' ')}`;
    return `${key}: ${value}`;
  }
  function removeChip(key: string, value: string) {
    if (key === 'workflow_status') toggleStatus(value); else applyBiFilter(key, null);
  }
  function clearAllFilters() {
    setWorkflowStatusFilter(''); setClosureReasonFilter(''); setSearchInput(''); setCommittedSearch('');
    setRequiresAction(false); setApproachingExpiry(false); setHasRenewals(undefined); setRiskCategoryFilter(''); setOffset(0);
  }

  function handleStatusChange(key: string) {
    const opt = STATUS_OPTIONS.find((o) => o.key === key) ?? STATUS_OPTIONS[0];
    setWorkflowStatusFilter(opt.workflow_status);
    setClosureReasonFilter(opt.closure_reason);
    setOffset(0);
  }

  // ORA.9: group items by renewal chain for list-page visual grouping
  const { groups, orphans } = groupOraItems(items);

  function handleAction(action: OraActionType, entry: OraEntry) {
    setActionModal({ action, entry });
  }

  return (
    <div className="space-y-5">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Risk Register</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Track organizational risk decisions, upcoming expirations, and items requiring attention.
          </p>
        </div>
        {canCreate && (
          <Button
            size="sm"
            onClick={() => navigate('/org-risk-acceptances/new')}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New Risk Response
          </Button>
        )}
      </div>

      {/* ── Search + active-filter chips (mirror vendor: removable chips live HERE, next to Search; the input
             stays free-text only. On-demand search fires on Search/Enter — card 10). ─────────── */}
      <div className="flex items-center gap-2 w-full md:max-w-[64%]">
        <div className={cn('flex-1 min-w-0 flex items-center gap-1.5 flex-wrap rounded-md border bg-bg-surface px-2.5 py-1.5 transition-colors',
          (committedSearch || chipEntries.length > 0) ? 'border-accent-cyan/60 ring-1 ring-accent-cyan/25' : 'border-border-subtle')}>
          {/* ⚠ active-filter chips — a KPI/strip click surfaces HERE (removable), NOT as text in the input. */}
          {chipEntries.map(([key, value]) => (
            <button key={`${key}:${value}`} type="button" onClick={() => removeChip(key, value)}
              className="shrink-0 inline-flex items-center gap-1 rounded border border-accent-cyan/30 bg-accent-cyan/10 px-1.5 py-0.5 text-[11px] font-medium text-accent-cyan hover:bg-accent-cyan/20 transition-colors">
              {chipLabel(key, value)}<X className="h-3 w-3" />
            </button>
          ))}
          <Search className="h-4 w-4 shrink-0 text-text-secondary" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
            maxLength={120}
            placeholder="Search title, description or justification…"
            className="flex-1 min-w-[140px] bg-transparent text-sm text-text-primary placeholder:text-text-secondary focus:outline-none py-0.5"
          />
          {committedSearch && (
            <button type="button" onClick={() => { setSearchInput(''); setCommittedSearch(''); setOffset(0); }}
              className="shrink-0 text-[11px] text-text-secondary hover:text-text-primary underline">Clear</button>
          )}
          {anyFilter && (
            <button type="button" onClick={clearAllFilters}
              className="shrink-0 ml-auto text-[11px] text-text-secondary hover:text-text-primary underline">Clear all</button>
          )}
        </div>
        <Button size="sm" onClick={submitSearch}>Search</Button>
        <Button size="sm" variant={requiresAction ? 'secondary' : 'outline'}
          onClick={() => { setRequiresAction((v) => !v); setOffset(0); }}>
          Requires my action
        </Button>
      </div>

      {/* ── RR-BI-1 Slice 2b: operational BI spine (KPIs · lifecycle · charts · attention) ─────────── */}
      <OrgRaOverviewSection
        overview={overview}
        loading={overviewLoading}
        statusSet={statusSet}
        closureReason={closureReasonFilter || null}
        requiresAction={requiresAction}
        onSetFilter={applyBiFilter}
        onToggleStatus={toggleStatus}
        onNavigate={(id) => navigate(`/org-risk-acceptances/${id}`)}
      />

      {/* ── Filter bar ───────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Status filter (⚠ O4a reads-flip: native lifecycle axis) */}
        <div className="flex items-center gap-2">
          <label htmlFor="status-filter" className="text-sm text-text-secondary whitespace-nowrap">
            Status:
          </label>
          <select
            id="status-filter"
            value={statusKeyFromFilter(workflowStatusFilter, closureReasonFilter)}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="h-8 rounded-md border border-border-default bg-bg-surface px-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-cyan"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Approaching Expiry smart filter */}
        <label className="flex items-center gap-1.5 text-sm text-text-secondary cursor-pointer select-none">
          <input
            type="checkbox"
            checked={approachingExpiry}
            onChange={(e) => { setApproachingExpiry(e.target.checked); setOffset(0); }}
            className="rounded border-border-default"
          />
          Approaching Expiry
        </label>

        {/* ⚠ RR-BI-1 final parity: the old "Clear filters" button is consolidated into "Clear all" in the
            search/chip box above (mirrors vendor — one clear affordance). */}

        {/* Page-size — pushed to the right */}
        <div className="ml-auto"><PageSizeSelect value={pageChoice} onChange={(n) => { setPageChoice(n); setOffset(0); }} /></div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-0">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 px-4 py-3 border-b border-border-subtle last:border-0"
                >
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-5 w-24 rounded-full" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            anyFilter ? (
              /* ⚠ card 9/12: zero results under filters — keep it distinct from the empty tenant */
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <ShieldAlert className="h-10 w-10 text-text-secondary/40 mb-3" />
                <p className="text-sm font-medium text-text-primary">No responses match these filters</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <ShieldAlert className="h-10 w-10 text-text-secondary/40 mb-3" />
                <p className="text-sm font-medium text-text-primary mb-1">
                  No Organizational Risk Responses yet.
                </p>
                <p className="text-xs text-text-secondary mb-4">
                  Create one to get started.
                </p>
                {canCreate && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/org-risk-acceptances/new')}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    New Risk Response
                  </Button>
                )}
              </div>
            )
          ) : (
            <>
              {/* Table header — ⚠ Slice 2: tracks MUST mirror OrgGroupRow exactly. The date twins are fixed 7rem
                  (right-anchored) so APPROVED/EXPIRES labels sit over their values; Title flexes, Status/Type auto. */}
              <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_auto_auto_7rem_7rem_2.5rem] gap-x-4 px-4 py-2.5 border-b border-border-subtle bg-bg-surface-2 rounded-t-lg text-xs font-medium text-text-secondary uppercase tracking-wide">
                <div>Title</div>
                <div>Status</div>
                <div>Type</div>
                <div>Approved</div>
                <div>Expires</div>
                <div className="w-6" />
              </div>

              {/* Rows — ORA.9: grouped by renewal chain */}
              <div>
                {groups.map(({ parent, children }) => (
                  <OrgRenewalChainGroup
                    key={parent.id}
                    parent={parent}
                    children={children}
                    isApproverClass={isApproverClass}
                    currentUserId={currentUserId}
                    onNavigate={(id) => navigate(`/org-risk-acceptances/${id}`)}
                    onAction={handleAction}
                  />
                ))}
                {orphans.map((entry) => (
                  <OrgOrphanChildRow
                    key={entry.id}
                    entry={entry}
                    isApproverClass={isApproverClass}
                    currentUserId={currentUserId}
                    onNavigate={(id) => navigate(`/org-risk-acceptances/${id}`)}
                    onAction={handleAction}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── "All" cap note — honest: the server caps at MAX_PAGE_SIZE, this is NOT the whole list (card 10) ── */}
      {pageChoice === 'All' && total > MAX_PAGE_SIZE && (
        <p className="text-xs italic text-text-faint">
          Showing up to {MAX_PAGE_SIZE} of {total} — the full list is paged. Narrow with search or filters to see the rest.
        </p>
      )}

      {/* ── Pagination ───────────────────────────────────────────────────── */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <span>
            Showing {offset + 1}–{Math.min(offset + pageSize, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset((p) => Math.max(0, p - pageSize))}
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </Button>
            <span className="text-xs">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + pageSize >= total}
              onClick={() => setOffset((p) => p + pageSize)}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Action modal ─────────────────────────────────────────────────── */}
      {actionModal && (
        <RiskAcceptanceActionModal
          action={actionModal.action}
          entry={actionModal.entry}
          onSuccess={() => setActionModal(null)}
          onClose={() => setActionModal(null)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mutationHook={useOrgRiskAcceptanceAction as any}
        />
      )}
    </div>
  );
}
